import React, { type FC, useState, useEffect, useMemo } from 'react';

import Button, { type ButtonProps } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Box, Inline, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Skeleton from '@atlaskit/skeleton';

import type { UserPickerUserPrincipal } from '../../commonTypes.tsx';
import { useUserSuggestions } from '../../../controller/queries/useUserSuggestions.ts';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller.tsx';

import { useUserSuggestionsMentionsExperiment } from './useUserSuggestionsMentionsExperiment.tsx';

const ENTRY_DURATION = 0.24;
const EXIT_DURATION = 0.12;
const MAX_SUGGESTIONS = 3;

const containerStyles = xcss({
	minHeight: '32px',
	marginTop: 'space.025',
	width: 'calc(100% + 8px)',
	position: 'relative',
	top: '-4px',
	left: '-4px',
});

const entryStyles = xcss({
	flex: '0 1 auto',
	maxWidth: '42%',
	opacity: 0,
	padding: 'space.050',
	transition: `opacity ${ENTRY_DURATION}s ease-in`,
});

const baseStyles = xcss({
	flex: '0 1 auto',
	maxWidth: '42%',
	opacity: 1,
	padding: 'space.050',
	overflow: 'hidden',
	transition: `opacity ${ENTRY_DURATION}s ease-in`,
});

const exitStyles = xcss({
	flex: '0 1 auto',
	maxWidth: '0%',
	opacity: 0,
	overflow: 'hidden',
	padding: 'space.0',
	transition: `all ${EXIT_DURATION}s ease-out`,
});

const skeletonWrapperStyles = xcss({
	height: '32px',
	padding: 'space.050',
});

interface SuggestedUserPillProps {
	user: UserPickerUserPrincipal;
	onClick: (user: UserPickerUserPrincipal) => void;
	indexPosition: number;
}

const SuggestedUserPill: FC<SuggestedUserPillProps> = ({ user, onClick, indexPosition }) => {
	const [animationState, setAnimationState] = useState('entry');
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleUserPillClick: ButtonProps['onClick'] = (_, buttonEvent) => {
		setAnimationState('exit');
		setTimeout(() => {
			onClick(user);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					...buttonEvent.payload,
					actionSubjectId: 'SuggestedShareUserButton',
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					attributes: {
						indexPosition: indexPosition + 1,
					},
				},
			}).fire();
		}, EXIT_DURATION * 1000);
	};

	useEffect(() => {
		setTimeout(() => {
			setAnimationState('base');
		});
	}, []);

	return (
		<Box
			xcss={
				animationState === 'entry'
					? entryStyles
					: animationState === 'exit'
						? exitStyles
						: baseStyles
			}
		>
			<Button
				spacing="compact"
				appearance="default"
				iconBefore={AddIcon}
				onClick={handleUserPillClick}
			>
				{user.name}
			</Button>
		</Box>
	);
};

const SuggestedUserPillLoader = () => (
	<Inline alignBlock="center" xcss={skeletonWrapperStyles}>
		<Skeleton width="148px" height="20px" borderRadius={3} />
	</Inline>
);
interface SuggestedShareUsersProps {
	currentPickedPrincipalIds: string[];
	onSuggestedUserClick: (selectedUser: UserPickerUserPrincipal) => void;
	defaultSuggestedUsers?: UserPickerUserPrincipal[] | null;
}

export const SuggestedShareUsers: FC<SuggestedShareUsersProps> = ({
	onSuggestedUserClick,
	currentPickedPrincipalIds,
	defaultSuggestedUsers,
}) => {
	const hasDefaultSuggestedUsers = !!(defaultSuggestedUsers && defaultSuggestedUsers.length > 0);

	const { contentId, spaceId, contextType, mentionsSessionId } =
		useUserSuggestionsMentionsExperiment();

	const { suggestedUsers: fetchedSuggestedUsers, loading } = useUserSuggestions({
		currentPickedPrincipalIds,
		contextType,
		containerId: spaceId,
		objectId: contentId,
		mentionsSessionId,
	});

	// Combine default users with fetched users
	const suggestedUsers = useMemo(() => {
		if (!hasDefaultSuggestedUsers) {
			// No default users, just use fetched users
			return fetchedSuggestedUsers;
		}

		// Filter out already picked users from default users
		const filteredDefaultUsers = defaultSuggestedUsers.filter(
			(user) => !currentPickedPrincipalIds.includes(user.id),
		);

		if (!fetchedSuggestedUsers) {
			// Only have default users, no fetched users yet
			return filteredDefaultUsers;
		}

		const defaultUserIds = filteredDefaultUsers.map((user) => user.id);

		const additionalUsers = fetchedSuggestedUsers.filter(
			(user) => !defaultUserIds.includes(user.id),
		);

		return [...filteredDefaultUsers, ...additionalUsers].slice(0, 3);
	}, [
		defaultSuggestedUsers,
		fetchedSuggestedUsers,
		hasDefaultSuggestedUsers,
		currentPickedPrincipalIds,
	]);

	const isInitialLoad = loading && suggestedUsers === null;

	if (!loading && suggestedUsers && suggestedUsers.length === 0) return null;

	return (
		<Flex
			wrap="nowrap"
			justifyContent="start"
			alignItems="center"
			direction="row"
			xcss={containerStyles}
		>
			{isInitialLoad
				? Array.from({ length: MAX_SUGGESTIONS }).map((_, i) => <SuggestedUserPillLoader key={i} />)
				: suggestedUsers?.map((user, index) => (
						<SuggestedUserPill
							key={user.id}
							user={user}
							indexPosition={index}
							onClick={onSuggestedUserClick}
						/>
					))}
		</Flex>
	);
};
